import { motion } from "framer-motion";
import { Globe, Network, Sparkles, FlaskConical, Eye, BookOpen, Share2, Handshake, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

export default function EcoSystem() {
  const ecosystemItems = [
    {
      href: "/ecosystem/juswork",
      icon: Sparkles,
      title: "JusWork",
      description: "Workforce pain diagnostics, support systems, and career transformation platform.",
      color: "from-orange-500 to-red-600"
    },
    {
      href: "/ecosystem/zodiac-lms-lab",
      icon: FlaskConical,
      title: "Zodiac LMS-Lab",
      description: "AI-enhanced learning, digital laboratories, and future education ecosystems.",
      color: "from-blue-500 to-indigo-600"
    },
    {
      href: "/ecosystem/observatory",
      icon: Eye,
      title: "Observatory",
      description: "Global workforce monitoring, intelligence dashboards, and predictive analytics.",
      color: "from-purple-500 to-violet-600"
    },
    {
      href: "/ecosystem/library",
      icon: BookOpen,
      title: "Library",
      description: "Digital knowledge systems, institutional archives, and workforce intelligence repositories.",
      color: "from-emerald-500 to-teal-600"
    },
    {
      href: "/ecosystem/social-media",
      icon: Share2,
      title: "Social-Media",
      description: "Media influence, communication systems, and institutional digital engagement.",
      color: "from-pink-500 to-rose-600"
    },
    {
      href: "/ecosystem/partnership",
      icon: Handshake,
      title: "Partnership & Global Network",
      description: "International collaboration, global institutional partnerships, and workforce diplomacy.",
      color: "from-amber-500 to-orange-600"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900" data-testid="ecosystem-page">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        {/* Animated Network Background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/3 left-1/4 w-64 h-64 bg-[#991B1B] rounded-full filter blur-[100px] animate-pulse" />
          <div className="absolute bottom-1/3 right-1/4 w-64 h-64 bg-[#1E3A8A] rounded-full filter blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-purple-600 rounded-full filter blur-[100px] animate-pulse" style={{ animationDelay: '2s' }} />
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/80 text-sm mb-6">
              <Network className="w-4 h-4" />
              <span>Integrated Ecosystem</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Zodiac <span className="text-[#991B1B]">Eco-System</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-white/70 max-w-3xl mx-auto mb-8">
              A comprehensive workforce intelligence ecosystem integrating platforms, 
              observatories, research, and global networks for institutional transformation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Ecosystem Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ecosystemItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link 
                  to={item.href}
                  className="group block h-full bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300 hover:scale-[1.02] hover:border-white/20"
                  data-testid={`ecosystem-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className={`w-16 h-16 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#991B1B] transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-white/60 mb-4">{item.description}</p>
                  <div className="flex items-center gap-2 text-[#991B1B] text-sm font-medium">
                    <span>Explore</span>
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Globe className="w-16 h-16 text-[#991B1B] mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-6">
              Globally Connected Intelligence
            </h2>
            <p className="text-white/60 mb-8">
              Our ecosystem integrates seamlessly across all platforms, providing unified 
              workforce intelligence and institutional transformation capabilities worldwide.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-[#991B1B] hover:bg-[#7f1d1d] text-white gap-2">
                  Connect With Us
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/programs">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 gap-2">
                  Explore Programs
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
